/**
 * Route-level providers. Re-exports store providers and provides wrapper
 * components for common provider combinations used in Routes.
 *
 * Usage in Route files:
 *   import { RoomAndPoll, RoomAndMessage } from '@/routes/providers';
 *   <Route path="..." element={<RoomAndPoll><SomePage /></RoomAndPoll>} />
 *
 * Or use individual providers from @/store and wrap in your Route element.
 */
import React from 'react';
import {
  RoomContextProvider,
  MessageContextProvider,
  PollContextProvider,
  EventContextProvider,
  ManagerNotificationContextProvider,
  ManagerUserContextProvider,
} from '@/store';
import { UserMonitorContextProvider } from '@/user-monitor';

// Re-export context providers from store so Routes can import from @/routes/providers
export {
  AuthContext,
  AuthContextProvider,
  ConferenceContext,
  ConferenceContextProvider,
  RoomContextProvider,
  ManagerConferenceContext,
  ManagerConferenceContextProvider,
  MessageContextProvider,
  PollContextProvider,
  ManagerContextProvider,
  OrganisationContextProvider,
  ManagerUserContextProvider,
  ManagerEntityContextProvider,
  ManagerEventContextProvider,
  ManagerNotificationContextProvider,
  ManagerPollContextProvider,
  ManagerSettingsContextProvider,
  ManagerTagContextProvider,
  ManagerRoomContextProvider,
  EventContextProvider,
} from '@/store';
export { UserMonitorContextProvider };

// --- Composed route wrappers (provider combinations used in Routes) ---

/** Room + Poll (e.g. ModeratorMultiScreen, ModeratorPollOnly, ModeratorQuizScreenshare) */
export function RoomAndPoll({ children }) {
  return (
    <RoomContextProvider>
      <PollContextProvider>{children}</PollContextProvider>
    </RoomContextProvider>
  );
}

/** Room + Message (e.g. AskModeratorsAndSpeakers, ModeratorPinned, ModeratorInteractions) */
export function RoomAndMessage({ children }) {
  return (
    <RoomContextProvider>
      <MessageContextProvider>{children}</MessageContextProvider>
    </RoomContextProvider>
  );
}

/** Room + Poll + Message (e.g. LiveRoom) */
export function RoomPollAndMessage({ children }) {
  return (
    <RoomContextProvider>
      <PollContextProvider>
        <MessageContextProvider>{children}</MessageContextProvider>
      </PollContextProvider>
    </RoomContextProvider>
  );
}

/** Event + Poll (e.g. Session) */
export function EventAndPoll({ children }) {
  return (
    <EventContextProvider>
      <PollContextProvider>{children}</PollContextProvider>
    </EventContextProvider>
  );
}

/** Manager notification + Manager user (e.g. EmailStart, Email, EmailEditAutoText) */
export function ManagerNotificationAndUser({ children }) {
  return (
    <ManagerNotificationContextProvider>
      <ManagerUserContextProvider>{children}</ManagerUserContextProvider>
    </ManagerNotificationContextProvider>
  );
}
